import { Analytics, gaGetClientIdSync } from "utils/analytics"
import { PERSONAL_INFO } from "utils/env-management"
import { getTildaUtm } from "utils/utm"
import { ErrorResponse, LastPsychologist } from "utils/interfaces"
import { isStorageAvailable, safeSetItem } from "utils/infrastructure/storage-util"
import { httpClient } from "utils/infrastructure/httpClient"

const SIGNED_IN_KEY = "alter_signed_in"

interface UserAccountData {
    name?: string
    email: string
    phone?: string
    utm: string
    source?: string
    ga_client_id: string | undefined
    b2b: boolean
    email_readonly: boolean
    company_inn?: string | null
    company_ooo?: string | null
    company_ogrn?: string | null
    company_address?: string | null
    psychologists?: LastPsychologist[]
}

class _UserAccount {
    storage: Storage | undefined

    constructor() {
        this.storage = isStorageAvailable ? window.localStorage : undefined
    }

    onSignUpQueue: Array<(data?: UserAccountData) => void> = []
    signOutQueue: Array<() => void> = []

    get isSignedIn() {
        return this.email || false
    }

    get data() {
        if (!this.storage) return undefined

        const data = this.storage?.getItem(SIGNED_IN_KEY)
        if (!data) return undefined
        return JSON.parse(data) as UserAccountData
    }

    get email() {
        return this.data ? this.data.email : undefined
    }

    get utm() {
        return this.data ? this.data.utm : undefined
    }

    get name() {
        return this.data ? this.data.name : undefined
    }

    get phone() {
        return this.data ? this.data.phone : undefined
    }

    get b2b() {
        return this.data ? this.data.b2b : false
    }

    get company_inn() {
        return this.data ? this.data.company_inn : undefined
    }

    get company_ooo() {
        return this.data ? this.data.company_ooo : undefined
    }

    get company_ogrn() {
        return this.data ? this.data.company_ogrn : undefined
    }

    get company_address() {
        return this.data ? this.data.company_address : undefined
    }

    makePersonalAgreementUrl(extraUrlencodedParams?: string) {
        const url = new URL(PERSONAL_INFO)

        if (this.company_inn && this.company_ooo) {
            url.searchParams.set("inn", this.company_inn)
            url.searchParams.set("ooo", this.company_ooo)
        }
        if (this.company_ogrn && this.company_address) {
            url.searchParams.set("ogrn", this.company_ogrn)
            url.searchParams.set("address", this.company_address)
        }
        if (this.b2b) url.searchParams.set("b2b", "1")

        if (extraUrlencodedParams) {
            const params = new URLSearchParams(extraUrlencodedParams)
            params.forEach((value, key) => {
                url.searchParams.append(key, value)
            })
        }

        return url.toString()
    }

    executeOnceWhenSignedIn(handler: () => void) {
        this.onSignUpQueue.push(handler)
    }

    onSignOut(handler: () => void) {
        this.signOutQueue.push(handler)
    }

    signUp(
        name: string,
        email: string,
        phone: string | undefined,
        source: string | undefined,
        onSuccess: (data: UserAccountData) => void,
        onFailure?: (data: ErrorResponse) => void
    ) {
        const gaData = { name, email }
        Analytics.send_client_signup(gaData)

        const data = {
            name,
            email,
            phone: phone,
            utm: getTildaUtm(),
            source,
            ga_client_id: gaGetClientIdSync(),
            b2b: false,
            email_readonly: false,
        }

        httpClient
            .post<ErrorResponse>("client_signup", data)
            .then(() => {
                this.storeSignIn(data)

                this.runSignUpQueue()

                onSuccess(data)
            })
            .catch((errorData: ErrorResponse) => {
                Analytics.client_signup_error(gaData)
                onFailure?.(errorData)
            })
    }

    signUpAfterSurvey(
        name: string,
        email: string,
        phone: string | undefined,
        source: string | undefined,
        resultsId: number | undefined,
        onSuccess: (data: UserAccountData) => void,
        onFailure?: (data: ErrorResponse) => void
    ) {
        const gaData = { name, email }
        Analytics.send_client_signup(gaData)

        const data = {
            name,
            email,
            phone: phone,
            utm: getTildaUtm(),
            source,
            ga_client_id: gaGetClientIdSync(),
            b2b: false,
            email_readonly: false,
            questionnaire_results_id: resultsId,
        }

        httpClient
            .post<ErrorResponse>("client/signup/after_survey", data)
            .then(() => {
                this.storeSignIn(data)

                this.runSignUpQueue()

                onSuccess(data)
            })
            .catch((errorData: ErrorResponse) => {
                Analytics.client_signup_error(gaData)
                onFailure?.(errorData)
            })
    }

    runSignUpQueue() {
        for (const handler of this.onSignUpQueue) {
            handler(this.data)
        }
        this.onSignUpQueue = []
    }

    storeSignIn(data: Partial<UserAccountData>) {
        if (!this.storage) return

        const dataToSave = Object.assign({}, data, {
            timestamp: new Date().toISOString(),
        })
        const cleanup = (key: string, value: unknown) => value ?? undefined
        const item = JSON.stringify(dataToSave, cleanup)

        safeSetItem(this.storage, SIGNED_IN_KEY, item)
    }

    signOut() {
        if (!this.storage) return
        this.storage?.removeItem(SIGNED_IN_KEY)
        this.signOutQueue.forEach((handler) => handler())
    }

    withUserData(json: object): object & { user_account: UserAccountData | undefined } {
        return {
            ...json,
            user_account: this.data,
        }
    }
}

export const UserAccount = new _UserAccount()
